import { Injectable } from "@angular/core";
import {
  BfxBaseBlotterContextMenuService,
  BfxBlotterActionEvent,
  BfxBlotterActionSubject,
} from "../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxBaseBlotter } from "app/bfx-base-blotter/bfx-base-blotter";
import {
  BfxBlotterContextMenuItem,
  BfxBlotterAction,
  TradeDealPromiseService,
  Roles,
  TradeDeal,
  ContextMenuItemState,
} from "shared/cubiq-services";
import { BfxRBACService } from "../../bfx-common/services/bfx-rbac.service";
import { BfxUserProfileService } from "../../bfx-common/services/bfx-user-profile.service";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

export class BfxBlotterTradeContextMenuItem extends BfxBlotterContextMenuItem<any> {
  constructor(
    public actionId: BfxBlotterAction,
    title: string = "",
    public iconClass?: string,
  ) {
    super(
      actionId,
      title,
      () => {
        return;
      },
      () => {
        return;
      },
    );
  }
}

export class BfxBlotterTradeDialogActionEvent extends BfxBlotterActionEvent {
  constructor(
    action: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds: Array<string>,
    selectedItems: Array<any>,
    public selectedTradeDeals: Array<TradeDeal>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ) {
    super(
      action,
      ladderBlotter,
      selectedIds,
      selectedItems,
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );
  }
}
import { LoggerService } from "@bfx/logger";

const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxTradeContextMenuService extends BfxBaseBlotterContextMenuService<any> {
  /**********************************************************************/
  /* DATA */
  public menuItems: Array<BfxBlotterTradeContextMenuItem>;

  public selectedTradeDeals: Array<TradeDeal> = [];

  protected permissions: any = {
    canConfirmTrade: null,
    canCancelTrade: null,
    canAddFillComment: null,
    canViewDetails: true,
    canViewTradeDetails: true,
    canExportTrade: true,
  };

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    protected tradeDealService: TradeDealPromiseService,
    protected rbacService: BfxRBACService,
    protected userProfileService: BfxUserProfileService,
  ) {
    super(new BfxBlotterActionSubject());
    this.initializePermissions();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initializePermissions() {
    // dynamic permissions
    Promise.all([
      this.rbacService.hasRole(Roles.DEAL_ACK),
      this.rbacService.hasRole(Roles.DEAL_CANCEL),
      this.rbacService.hasRole(Roles.ADD_FILL_COMMENT),
      this.rbacService.hasRole(Roles.ROLE_ORDER_EXPORT),
    ]).then((values) => {
      this.permissions.canConfirmTrade = values[0];
      this.permissions.canCancelTrade = values[1];
      this.permissions.canAddFillComment = values[2];
      this.permissions.canExportTrade = values[3];
      logger.out.info(
        "OMS BfxTradeContextMenuService.initializePermissionsLocal(): permissions ",
        this.permissions,
      );
      this.initMenuItems();
    });
  }

  /**
   *  // can perform CANCEL
   *       States with comments are allowed for cancel
   *       OrderDealState.ACTIVE, //STP In Progress - disable
   *       OrderDealState.FILL_ERROR, // STP Fill Error
   *       OrderDealState.COMPLETED, //  STP Confirmed
   *       OrderDealState.CANCELLED,
   *       OrderDealState.ACK_TIME_OUT, //STP Time Out
   *       OrderDealState.NAK, // STP NAK
   *       OrderDealState.DONE_NO_STP, // STP Disabled
   *       OrderDealState.STP_HOLD // Hold STP
   */
  protected getEnabledActions(
    items: Array<any>,
    selectedOrderIds: Array<string>,
  ): Array<BfxBlotterAction> {
    let tradesCount: number = items.length;
    let enabledActionIds: Array<BfxBlotterAction> = [];

    let confirmConditions: {
      notConfirmed: boolean;
      notInProgress: boolean;
      inSalesGroups: boolean;
      notSalesUser: boolean;
    } = {
      notConfirmed: items.length > 0,
      notInProgress: items.length > 0,
      inSalesGroups: this.userProfileService.isSalesUser(),
      notSalesUser: !this.userProfileService.isSalesUser(),
    };

    let cancelConditions: {
      notCanceled: boolean;
      inSalesGroups: boolean;
      notSalesUser: boolean;
    } = {
      notCanceled: items.length > 0,
      inSalesGroups: this.userProfileService.isSalesUser(),
      notSalesUser: !this.userProfileService.isSalesUser(),
    };

    let fillPending: boolean = false;

    // can perform CONFIRM
    items.forEach((deal) => {
      const salesGroupId = deal.salesGroupId?.toString() ?? "";
      confirmConditions.notConfirmed =
        confirmConditions.notConfirmed &&
        (deal.confirmUserId === null || deal.confirmUserId === undefined);
      confirmConditions.notInProgress =
        confirmConditions.notInProgress &&
        deal.dealStateId &&
        deal.dealStateId !== CubiqTS.OrderDealState.ACTIVE;
      if (confirmConditions.inSalesGroups) {
        confirmConditions.inSalesGroups =
          confirmConditions.inSalesGroups &&
          salesGroupId &&
          this.userProfileService.isMemberOfSalesGroup(salesGroupId);
      }

      if (CubiqTS.OrderDealState.FILL_PENDING == deal.dealStateId) {
        fillPending = true;
      }
    });

    if (
      !fillPending &&
      confirmConditions.notConfirmed &&
      confirmConditions.notInProgress &&
      this.permissions.canConfirmTrade &&
      (confirmConditions.notSalesUser || confirmConditions.inSalesGroups)
    ) {
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_CONFIRM);
    }

    // Can perform CANCEL
    items.forEach((deal) => {
      const salesGroupId = deal.salesGroupId?.toString() ?? "";
      cancelConditions.notCanceled =
        cancelConditions.notCanceled &&
        deal.dealStateId &&
        deal.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
        deal.dealStateId !== CubiqTS.OrderDealState.ACTIVE &&
        deal.dealStateId !== CubiqTS.OrderDealState.NAK &&
        deal.dealStateId !== CubiqTS.OrderDealState.ACK_TIME_OUT;
      if (cancelConditions.inSalesGroups) {
        cancelConditions.inSalesGroups =
          cancelConditions.inSalesGroups &&
          salesGroupId &&
          this.userProfileService.isMemberOfSalesGroup(salesGroupId);
      }
    });

    if (
      !fillPending &&
      cancelConditions.notCanceled &&
      this.permissions.canCancelTrade &&
      (cancelConditions.notSalesUser || cancelConditions.inSalesGroups)
    ) {
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_CANCEL);
    }

    // OTHER
    if (tradesCount == 1) {
      if (!fillPending) {
        enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_ADD_FILL_COMMENT);
      }
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_VIEW_DETAILS);
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS);
    }

    if (tradesCount != 0) {
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_EXPORT);
    }

    return enabledActionIds;
  }

  protected getActionDecorators(items: Array<any>): any {
    // nothing to add
  }

  protected prepareActions(ladderBlotter: BfxBaseBlotter): void {
    // nothing to add
  }

  public getMenuItems(): Array<BfxBlotterTradeContextMenuItem> {
    if (!this.menuItems) {
      this.initMenuItems();
    }
    return this.menuItems;
  }

  public buildMenu(
    blotter: BfxBaseBlotter,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<object> | null,
  ): void {
    this.tradeDealService
      .adaptTradeDeals(selectedItems)
      .then((selectedTradeDeals: Array<TradeDeal>) => {
        this.selectedTradeDeals = selectedTradeDeals;
        let enabledItems: Array<BfxBlotterAction> = this.getEnabledActions(
          selectedTradeDeals,
          selectedIds,
        );
        let actionDecorators: any = this.getActionDecorators(selectedTradeDeals);

        this.showMenuAt(blotter, pageX, pageY, enabledItems, actionDecorators);
      });
  }

  protected initMenuItems(): void {
    this.menuItems = [];

    if (this.permissions.canConfirmTrade) {
      this.menuItems.push(
        new BfxBlotterTradeContextMenuItem(BfxBlotterAction.ACTION_TRADE_CONFIRM, "Confirm Trade"),
      );
    }

    if (this.permissions.canCancelTrade) {
      this.menuItems.push(
        new BfxBlotterTradeContextMenuItem(
          BfxBlotterAction.ACTION_TRADE_CANCEL,
          "Flag Trade as Cancelled",
        ),
      );
    }
    if (this.permissions.canAddFillComment) {
      this.menuItems.push(
        new BfxBlotterTradeContextMenuItem(
          BfxBlotterAction.ACTION_TRADE_ADD_FILL_COMMENT,
          "Add Fill Comment",
        ),
      );
    }
    if (this.permissions.canViewDetails) {
      this.menuItems.push(
        new BfxBlotterTradeContextMenuItem(
          BfxBlotterAction.ACTION_TRADE_VIEW_DETAILS,
          "View Details",
        ),
      );
    }
    if (this.permissions.canViewTradeDetails) {
      this.menuItems.push(
        new BfxBlotterTradeContextMenuItem(
          BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS,
          "View Trade Details",
        ),
      );
    }
    this.menuItems.push(new BfxBlotterTradeContextMenuItem(BfxBlotterAction.SEPARATOR));
    if (this.permissions.canExportTrade) {
      this.menuItems.push(
        new BfxBlotterTradeContextMenuItem(BfxBlotterAction.ACTION_TRADE_EXPORT, "Export to CSV"),
      );
    }
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterTradeDialogActionEvent | BfxBlotterActionEvent;

    switch (actionId) {
      case BfxBlotterAction.ACTION_TRADE_CONFIRM:
      case BfxBlotterAction.ACTION_TRADE_CANCEL:
      case BfxBlotterAction.ACTION_TRADE_ADD_FILL_COMMENT:
        // only doMenu sets up this.selectedTradeDeals correctly
        event = new BfxBlotterTradeDialogActionEvent(
          actionId,
          ladderBlotter,
          this.getSelectedIds(selectedIds, ladderBlotter),
          this.getSelectedItems(selectedItems, ladderBlotter),
          this.selectedTradeDeals,
          itemState,
          hide,
          menuBoundingClientRect,
          actionEvent,
        );
        break;
      case BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS:
        {
          let ids = this.getSelectedIds(selectedIds, ladderBlotter);
          let items = this.getSelectedItems(selectedItems, ladderBlotter);

          if (ladderBlotter.checkViewTradeDetailItems != null) {
            ladderBlotter.checkViewTradeDetailItems(items, ids);
          }

          event = new BfxBlotterActionEvent(
            actionId,
            ladderBlotter,
            ids,
            items,
            itemState,
            hide,
            menuBoundingClientRect,
            actionEvent,
          );
        }
        break;
      default:
        event = new BfxBlotterActionEvent(
          actionId,
          ladderBlotter,
          this.getSelectedIds(selectedIds, ladderBlotter),
          this.getSelectedItems(selectedItems, ladderBlotter),
          itemState,
          hide,
          menuBoundingClientRect,
          actionEvent,
        );
        break;
    }

    this.actionSubject.next(event);
  }
}
